import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useTheme } from "../contexts/ThemeContext";
import { Sparkles, Zap, FlaskConical, Store, Maximize2, ArrowLeft, Sun, Moon } from "lucide-react";
import "../styles/swarm-theme.css";
import { OutputPanel, FindingDetail, money } from "../components/swarm/OutputPanel";
import { TaskSidebar, ChatDock, AgentTooltip, StorePanel, FullChat } from "../components/swarm/SwarmChrome";
import SwarmTopControls from "@/components/projects/SwarmTopControls";
import SwarmWorkspace from "@/components/projects/SwarmWorkspace";
import SwarmProjectView from "@/components/projects/SwarmProjectView";
import ProjectRunBanner from "@/components/projects/ProjectRunBanner";
import { MarkdownDoc, resultToMarkdown } from "../components/swarm/SwarmDoc";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DUR = 14;
const MONO = "'JetBrains Mono', ui-monospace, monospace";

const PHASES = [
  { key: "ingest", label: "Prompt ingestion", a: 0.0, b: 0.14 },
  { key: "collect", label: "Data collection", a: 0.14, b: 0.42 },
  { key: "score", label: "Analysis & scoring", a: 0.42, b: 0.62 },
  { key: "quantum", label: "Quantum optimization", a: 0.62, b: 0.82 },
  { key: "assemble", label: "Output assembly", a: 0.82, b: 1.0 },
];

const RING_ORDER = ["QC", "OO", "CX", "PI", "WO", "SC", "MI", "IO"];
const PHI = (1 + Math.sqrt(5)) / 2;
const ICO_V = [[-1, PHI, 0], [1, PHI, 0], [-1, -PHI, 0], [1, -PHI, 0], [0, -1, PHI], [0, 1, PHI],
  [0, -1, -PHI], [0, 1, -PHI], [PHI, 0, -1], [PHI, 0, 1], [-PHI, 0, -1], [-PHI, 0, 1]];
const ICO_SCALE = 1 / Math.sqrt(1 + PHI * PHI);
const ICO_E = (() => {
  const e = []; const eq = 2.0;
  for (let i = 0; i < ICO_V.length; i++) for (let j = i + 1; j < ICO_V.length; j++) {
    const dx = ICO_V[i][0] - ICO_V[j][0], dy = ICO_V[i][1] - ICO_V[j][1], dz = ICO_V[i][2] - ICO_V[j][2];
    if (Math.abs(dx * dx + dy * dy + dz * dz - eq * eq) < 0.4) e.push([i, j]);
  } return e;
})();

const PROMPTS = {
  retail: ["Analyze inventory allocation across all 700 stores for Q3 optimization",
           "Optimize size curves for our Southeast region based on local demographics",
           "What is the optimal pre-positioning strategy for wedding season 2026?"],
  jnj: ["Optimize clinical trial site selection for our Phase III oncology compound",
        "Identify cold chain routing inefficiencies across our biologics portfolio",
        "Rebalance sales rep territories to maximize HCP coverage in cardiology"],
};

const now12 = () => new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

export default function SwarmConstellation() {
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const stateRef = useRef({ divisions: [], nodes: [], run: null, hover: null, mouse: { x: 0, y: 0 }, w: 0, h: 0, detail: {} });
  const [roster, setRoster] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [running, setRunning] = useState(false);
  const [phaseLabel, setPhaseLabel] = useState("");
  const [statusText, setStatusText] = useState("");
  const [liveCost, setLiveCost] = useState(0);
  const [liveAgents, setLiveAgents] = useState(0);
  const [result, setResult] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [hoverAgent, setHoverAgent] = useState(null);
  const [mode, setMode] = useState(() => {
    const m = new URLSearchParams(window.location.search).get("mode");
    return m === "jnj" ? "jnj" : "retail";
  });
  const [role, setRole] = useState("ceo");

  // Sync mode when the URL query changes (e.g. clicking the Men's Wearhouse vs Johnson & Johnson tab)
  useEffect(() => {
    if (running) return;
    const m = new URLSearchParams(location.search).get("mode");
    setMode(m === "jnj" ? "jnj" : "retail");
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const [tasks, setTasks] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [fullChat, setFullChat] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(560);
  const { resolvedTheme, setTheme } = useTheme();
  const theme = resolvedTheme;
  useEffect(() => { stateRef.current.theme = theme; }, [theme]);
  const [attached, setAttached] = useState(null);
  const [storeOpen, setStoreOpen] = useState(false);
  const [storeCards, setStoreCards] = useState([]);
  const [storeMeta, setStoreMeta] = useState({ regions: [], action_types: [] });
  const [storeFilter, setStoreFilter] = useState({ region: "", brand: "", priority: "", q: "" });
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [routingMode, setRoutingMode] = useState("smart");
  const [exporting, setExporting] = useState(false);
  const [openProjectId, setOpenProjectId] = useState(null);
  const [workspaceKey, setWorkspaceKey] = useState(0);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const swarmKey = mode === "jnj" ? "pharmaceutical" : "retail";

  const sessionStats = useMemo(() => ({
    queries: tasks.length,
    cost: tasks.reduce((s, t) => s + (t.cost || 0), 0),
    value: tasks.reduce((s, t) => s + (t.uplift || 0), 0),
    agents: tasks.length * 200,
  }), [tasks]);

  useEffect(() => {
    axios.get(`${API}/swarm/roster?mode=${mode}`).then(({ data }) => { setRoster(data); layout(data.divisions); });
    setResult(null); setPanelOpen(false); setStoreOpen(false); setMessages([]); setActiveId(null);
    stateRef.current.detail = {};
  }, [mode]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/swarm/history`);
      setTasks((data.runs || []).map((r) => ({
        id: r.run_id, prompt: r.prompt, label: r.label || r.prompt,
        time: new Date(r.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
        status: "completed", mode: r.mode, uplift: r.uplift, cost: r.cost,
      })));
    } catch (_) {}
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    const pid = new URLSearchParams(window.location.search).get("project");
    if (pid) setOpenProjectId(pid);
  }, []);

  const layout = (divisions) => {
    const S = stateRef.current;
    const w = S.w || (stageRef.current?.clientWidth ?? window.innerWidth);
    const h = S.h || (stageRef.current?.clientHeight ?? window.innerHeight);
    const cx = w / 2, cy = h / 2, coreR = 56;
    const outerR = Math.max(180, Math.min(w, h) / 2 - 46), innerR = coreR + 54;
    const nodes = [];
    const ordered = [...divisions].sort((a, b) => RING_ORDER.indexOf(a.code) - RING_ORDER.indexOf(b.code));
    const n = ordered.length;
    ordered.forEach((div, ri) => {
      const ringR = innerR + (outerR - innerR) * (n > 1 ? ri / (n - 1) : 0);
      div._ring = ringR; div._speed = (0.03 + (ri % 3) * 0.018) * (ri % 2 === 0 ? 1 : -1);
      div._phase0 = (ri * 0.7) % (Math.PI * 2);
      const count = div.agents.length;
      div.agents.forEach((ag, ai) => {
        nodes.push({ ...ag, color: div.color, ringR, baseAngle: div._phase0 + (ai * 2 * Math.PI) / count,
          spd: div._speed, x: cx, y: cy, nodePhase: Math.random() * Math.PI * 2, hexRot: Math.random() * Math.PI });
      });
    });
    S.divisions = divisions; S.nodes = nodes;
  };

  useEffect(() => {
    const canvas = canvasRef.current, stage = stageRef.current;
    if (!canvas || !stage) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2), S = stateRef.current;
    const resize = () => {
      const w = stage.clientWidth, h = stage.clientHeight; S.w = w; S.h = h;
      canvas.width = w * dpr; canvas.height = h * dpr; canvas.style.width = w + "px"; canvas.style.height = h + "px";
      if (S.divisions.length) layout(S.divisions);
    };
    resize();
    const ro = new ResizeObserver(resize); ro.observe(stage);
    const ctx = canvas.getContext("2d"); let raf; const start = performance.now();
    // cinematic film-grain tile (built once)
    const grain = document.createElement("canvas"); grain.width = 220; grain.height = 220;
    const gctx = grain.getContext("2d"); const gi = gctx.createImageData(220, 220);
    for (let i = 0; i < gi.data.length; i += 4) { const v = Math.random() * 255 | 0; gi.data[i] = gi.data[i + 1] = gi.data[i + 2] = v; gi.data[i + 3] = 255; }
    gctx.putImageData(gi, 0, 0);
    const hex = (x, y, r, rot) => { ctx.beginPath(); for (let i = 0; i < 6; i++) { const a = rot + i * Math.PI / 3; const px = x + Math.cos(a) * r, py = y + Math.sin(a) * r; i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py); } ctx.closePath(); };

    const draw = (nowt) => {
      const t = (nowt - start) / 1000, w = S.w, h = S.h, cx = w / 2, cy = h / 2;
      const light = S.theme === "light";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const g = ctx.createRadialGradient(cx, cy, 40, cx, cy, Math.max(cx, cy) * 1.35);
      if (light) { g.addColorStop(0, "#FFFFFF"); g.addColorStop(0.6, "#F4F7FA"); g.addColorStop(1, "#E9EEF3"); }
      else { g.addColorStop(0, "#0C1220"); g.addColorStop(0.6, "#080D18"); g.addColorStop(1, "#05070E"); }
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      // ── cinematic backdrop: drifting nebula blooms + volumetric god-rays ──
      ctx.globalCompositeOperation = "lighter";
      const b1x = cx + Math.sin(t * 0.05) * w * 0.12, b1y = cy + Math.cos(t * 0.04) * h * 0.10;
      let bl = ctx.createRadialGradient(b1x, b1y, 0, b1x, b1y, Math.max(w, h) * 0.55);
      bl.addColorStop(0, "rgba(16,120,180,0.10)"); bl.addColorStop(1, "rgba(16,120,180,0)");
      ctx.fillStyle = bl; ctx.fillRect(0, 0, w, h);
      const b2x = cx - Math.cos(t * 0.045) * w * 0.14, b2y = cy - Math.sin(t * 0.05) * h * 0.12;
      let bl2 = ctx.createRadialGradient(b2x, b2y, 0, b2x, b2y, Math.max(w, h) * 0.5);
      bl2.addColorStop(0, "rgba(139,92,246,0.08)"); bl2.addColorStop(1, "rgba(139,92,246,0)");
      ctx.fillStyle = bl2; ctx.fillRect(0, 0, w, h);
      ctx.save(); ctx.translate(cx, cy);
      const rays = 9, rlen = Math.max(w, h) * 0.95;
      for (let i = 0; i < rays; i++) {
        const a = t * 0.03 + i * (Math.PI * 2 / rays), sp = 0.055;
        const rg = ctx.createLinearGradient(0, 0, Math.cos(a) * rlen, Math.sin(a) * rlen);
        rg.addColorStop(0, "rgba(20,240,200,0.05)"); rg.addColorStop(1, "rgba(20,240,200,0)");
        ctx.fillStyle = rg; ctx.beginPath(); ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(a - sp) * rlen, Math.sin(a - sp) * rlen);
        ctx.lineTo(Math.cos(a + sp) * rlen, Math.sin(a + sp) * rlen);
        ctx.closePath(); ctx.fill();
      }
      ctx.restore(); ctx.globalCompositeOperation = "source-over";
      let energy = 1, phaseFrac = -1, activeSet = new Set(), coreFlare = 0;
      if (S.run) {
        const rt = (nowt - S.run.startedAt) / 1000; phaseFrac = Math.min(1, rt / DUR);
        if (!S.run.data && phaseFrac > 0.8) phaseFrac = 0.8 + 0.02 * Math.sin(rt * 3);
        activeSet = new Set(S.run.active);
        const q = PHASES[3];
        if (phaseFrac >= q.a && phaseFrac <= q.b) coreFlare = Math.sin(((phaseFrac - q.a) / (q.b - q.a)) * Math.PI);
        if (phaseFrac >= 1 && S.run.data) finishRun(S);
        if (S.run.settledAt) { const settle = Math.min(1, (nowt - S.run.settledAt) / 1600); energy = 1 - 0.9 * settle; coreFlare = 0; }
      }
      ctx.strokeStyle = light ? "rgba(100,116,139,0.18)" : "rgba(148,163,184,0.06)"; ctx.lineWidth = 1;
      S.divisions.forEach((d) => { if (!d._ring) return; ctx.beginPath(); ctx.ellipse(cx, cy, d._ring, d._ring * 0.86, 0, 0, Math.PI * 2); ctx.stroke(); });
      if (S.run && phaseFrac > PHASES[1].a) {
        S.divisions.forEach((div) => {
          if (!activeSet.has(div.code) || div.code === "QC" || !div._ring) return;
          const ang = div._phase0 + t * div._speed * energy;
          const ax = cx + Math.cos(ang) * div._ring, ay = cy + Math.sin(ang) * div._ring * 0.86;
          ctx.strokeStyle = div.color + Math.round(40 * energy + 10).toString(16).padStart(2, "0"); ctx.lineWidth = 1.3;
          ctx.beginPath(); ctx.moveTo(ax, ay);
          const mx = (ax + cx) / 2 + (ay - cy) * 0.18, my = (ay + cy) / 2 - (ax - cx) * 0.18;
          ctx.quadraticCurveTo(mx, my, cx, cy); ctx.stroke();
          if (energy > 0.5) for (let p = 0; p < 5; p++) { const fp = ((t * 0.5 + p / 5) % 1), u = 1 - fp; const px = u * u * ax + 2 * u * fp * mx + fp * fp * cx; const py = u * u * ay + 2 * u * fp * my + fp * fp * cy; ctx.beginPath(); ctx.fillStyle = div.color; ctx.arc(px, py, 2.2, 0, Math.PI * 2); ctx.fill(); }
        });
      }
      S.hover = null;
      S.nodes.forEach((n) => {
        const ang = n.baseAngle + t * n.spd * energy;
        n.x = cx + Math.cos(ang) * n.ringR; n.y = cy + Math.sin(ang) * n.ringR * 0.86;
        const isActive = S.run && activeSet.has(n.division) && phaseFrac > PHASES[1].a && !S.run.settledAt;
        const breathe = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(t * 2 + n.nodePhase)) * energy;
        const r = isActive ? 6 : 3.6;
        ctx.shadowBlur = isActive ? 16 : 6 * energy + 2; ctx.shadowColor = n.color;
        ctx.globalAlpha = isActive ? 0.96 : breathe * 0.85; ctx.fillStyle = n.color;
        hex(n.x, n.y, r, n.hexRot + t * 0.3 * energy); ctx.fill();
        if (isActive && phaseFrac < PHASES[3].b) { ctx.globalAlpha = 0.7; ctx.shadowBlur = 0; ctx.strokeStyle = n.color; ctx.lineWidth = 1.2; ctx.beginPath(); ctx.arc(n.x, n.y, r + 4, t * 3 + n.nodePhase, t * 3 + n.nodePhase + 1.7); ctx.stroke(); }
        const dx = S.mouse.x - n.x, dy = S.mouse.y - n.y; if (dx * dx + dy * dy < 110) S.hover = n;
        ctx.globalAlpha = 1; ctx.shadowBlur = 0;
      });
      const coreR = 52 + coreFlare * 22, spin = t * (0.28 + coreFlare * 2.4) * (energy < 0.5 ? 0.3 : 1), tilt = 0.5 + 0.15 * Math.sin(t * 0.4);
      const halo = ctx.createRadialGradient(cx, cy, 4, cx, cy, coreR * 2.6);
      halo.addColorStop(0, `rgba(226,232,240,${0.85 * (0.5 + 0.5 * energy)})`); halo.addColorStop(0.3, `rgba(20,240,200,${0.35 + coreFlare * 0.5})`); halo.addColorStop(1, "rgba(20,240,200,0)");
      ctx.fillStyle = halo; ctx.beginPath(); ctx.arc(cx, cy, coreR * 2.6, 0, Math.PI * 2); ctx.fill();
      const cY = Math.cos(spin), sY = Math.sin(spin), cX = Math.cos(tilt), sX = Math.sin(tilt);
      const proj = ICO_V.map(([x, y, z]) => { const X = x * cY - z * sY, Z = x * sY + z * cY; const Y2 = y * cX - Z * sX, Z2 = y * sX + Z * cX; const s = coreR * ICO_SCALE, depth = (Z2 * ICO_SCALE + 1.9) / 3.8; return { x: cx + X * s, y: cy + Y2 * s, d: depth }; });
      ctx.shadowBlur = 14; ctx.shadowColor = "#14F0C8";
      ICO_E.forEach(([i, j]) => { const a = proj[i], b = proj[j], dep = (a.d + b.d) / 2; ctx.strokeStyle = `rgba(${light ? "11,143,163" : "190,255,240"},${(0.25 + 0.6 * dep) * (0.55 + 0.45 * energy)})`; ctx.lineWidth = 0.6 + 1.6 * dep; ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); });
      ctx.shadowBlur = 0; proj.forEach((p) => { ctx.globalAlpha = 0.4 + 0.6 * p.d; ctx.fillStyle = light ? "#0B7A8C" : "#E6FFF9"; ctx.beginPath(); ctx.arc(p.x, p.y, 0.8 + 1.8 * p.d, 0, Math.PI * 2); ctx.fill(); }); ctx.globalAlpha = 1;
      ctx.fillStyle = `rgba(20,240,200,${0.5 + coreFlare * 0.4})`; ctx.shadowBlur = 24; ctx.shadowColor = "#14F0C8"; ctx.beginPath(); ctx.arc(cx, cy, 6 + coreFlare * 5, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
      if (coreFlare > 0.85) { ctx.globalAlpha = (coreFlare - 0.85) / 0.15; ctx.strokeStyle = "#14F0C8"; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(cx, cy, coreR + (coreFlare - 0.85) * 800, 0, Math.PI * 2); ctx.stroke(); ctx.globalAlpha = 1; }
      if (S.run && phaseFrac >= 0 && phaseFrac < PHASES[1].b && energy > 0.5) {
        for (let p = 0; p < 16; p++) { const fp = ((t * 0.8 + p / 16) % 1); const sx = cx + (p - 8) * 9, sy = h - 20; const px = sx + (cx - sx) * fp, py = sy + (cy - sy) * fp; ctx.globalAlpha = (1 - fp) * 0.9; ctx.fillStyle = "#14F0C8"; ctx.beginPath(); ctx.arc(px, py, 2, 0, Math.PI * 2); ctx.fill(); } ctx.globalAlpha = 1;
      }
      // ── cinematic foreground grade: lens flare · film grain · vignette · letterbox ──
      ctx.save(); ctx.globalCompositeOperation = "lighter";
      const fa = 0.6, diag = Math.max(w, h) * 0.5;
      for (const [d, r, al] of [[-0.35, 55, 0.05], [0.28, 95, 0.04], [0.55, 38, 0.06], [0.82, 130, 0.028]]) {
        const fx = cx + Math.cos(fa) * d * diag, fy = cy + Math.sin(fa) * d * diag;
        const fg = ctx.createRadialGradient(fx, fy, 0, fx, fy, r);
        fg.addColorStop(0, `rgba(200,255,244,${al})`); fg.addColorStop(1, "rgba(200,255,244,0)");
        ctx.fillStyle = fg; ctx.beginPath(); ctx.arc(fx, fy, r, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
      ctx.globalCompositeOperation = "overlay"; ctx.globalAlpha = 0.09;
      ctx.drawImage(grain, (Math.random() * 40 - 20) | 0, (Math.random() * 40 - 20) | 0, w + 40, h + 40);
      ctx.globalAlpha = 1; ctx.globalCompositeOperation = "source-over";
      const vg = ctx.createRadialGradient(cx, cy, Math.min(w, h) * 0.34, cx, cy, Math.max(w, h) * 0.78);
      vg.addColorStop(0, "rgba(0,0,0,0)"); vg.addColorStop(1, light ? "rgba(180,195,210,0.35)" : "rgba(0,0,0,0.55)");
      ctx.fillStyle = vg; ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = light ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.32)"; ctx.fillRect(0, 0, w, 16); ctx.fillRect(0, h - 16, w, 16);
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishRun = useCallback((S) => {
    if (!S.run || S.run.finished) return;
    S.run.finished = true; S.run.settledAt = performance.now();
    const data = S.run.data;
    setResult(data); setRunning(false); setPhaseLabel("Complete"); setStatusText("");
    if (data?.roster_detail) { const m = {}; data.roster_detail.forEach((a) => { m[a.id] = a; }); S.detail = m; }
    if (data) {
      const id = data.run_id;
      const cost = data.cost_intelligence?.actual_cost || 0;
      setActiveId(id);
      try { sessionStorage.setItem("qnneal_res_" + id, JSON.stringify(data)); } catch (_) {}
      setTasks((prev) => prev.map((t) => t.id === S.run.taskId
        ? { ...t, id, status: "completed", uplift: data.summary?.quantum_uplift_usd, cost } : t));
      setMessages((prev) => [...prev,
        { role: "assistant", text: (data.quantum_summary?.callout ? data.quantum_summary.callout + "\n\n" : "") + (data.executive_summary || "Analysis complete.") },
        { kind: "doc", runId: id, title: "Quantum Optimization Report", uplift: data.summary?.quantum_uplift_usd }]);
      setFullChat(true); setPanelOpen(false);
      loadHistory();
      setWorkspaceKey((k) => k + 1);
    }
  }, [loadHistory]);

  const runOptimize = async (text, projectId) => {
    const p = (text ?? prompt).trim(); if (!p || running) return;
    setOpenProjectId(null);
    const taskId = "t_" + Math.random().toString(36).slice(2, 9);
    setResult(null); setPanelOpen(false); setRunning(true); setStoreOpen(false); setSelectedFinding(null); setFullChat(false); setDocOpen(null);
    setMessages((prev) => [...prev, { role: "user", text: p, attachment: attached?.filename }]);
    setTasks((prev) => [{ id: taskId, prompt: p, time: now12(), status: "active", mode }, ...prev]);
    setActiveId(taskId);
    setPrompt(""); setLiveCost(0); setLiveAgents(0);
    const guess = ["MI", "IO", "QC", "OO"];
    stateRef.current.run = { startedAt: performance.now(), active: guess, data: null, finished: false, settledAt: null, taskId };
    setPhaseLabel(PHASES[0].label); setStatusText(`Activating agents across ${guess.length} divisions`);
    const tick = setInterval(() => {
      const R = stateRef.current.run; if (!R) return clearInterval(tick);
      const f = ((performance.now() - R.startedAt) / 1000) / DUR;
      const idx = PHASES.findIndex((ph) => f >= ph.a && f < ph.b);
      const d = R.data;
      const target = d?.cost_intelligence?.actual_cost || 0.55;
      setLiveCost(+(target * Math.min(1, f)).toFixed(2));
      setLiveAgents(Math.round((d?.active_agent_count || 120) * Math.min(1, f / 0.5)));
      const STATUS = {
        ingest: `Activating ${d?.active_agent_count || 75} agents across ${(d?.active_divisions || guess).length} divisions`,
        collect: "Analyzing SKU-store combinations", score: "Scoring positions against demand forecasts",
        quantum: d ? `Quantum solver: ${d.compute_method?.variables || 144} variables · solved in ${d.compute_method?.solve_time_ms}ms` : "Quantum solver running on live optimizer…",
        assemble: "Assembling executive deliverable",
      };
      if (idx >= 0) { setPhaseLabel(PHASES[idx].label); setStatusText(STATUS[PHASES[idx].key]); }
      if (f >= 1 && d) clearInterval(tick);
    }, 200);
    try {
      const { data } = await axios.post(`${API}/swarm/execute`, { prompt: p, mode, file_text: attached?.excerpt || "", project_id: projectId || undefined });
      if (stateRef.current.run) { stateRef.current.run.data = data; stateRef.current.run.active = data.active_divisions; }
    } catch (e) {
      try {
        const { data } = await axios.post(`${API}/swarm/optimize`, { prompt: p });
        if (stateRef.current.run) { stateRef.current.run.data = data; stateRef.current.run.active = data.active_divisions; }
      } catch (_) { setRunning(false); }
    }
  };

  const sendFollowup = async (text) => {
    const p = text.trim(); if (!p || !result) return;
    setPrompt("");
    setMessages((prev) => [...prev, { role: "user", text: p }, { role: "assistant", pending: true }]);
    try {
      const { data } = await axios.post(`${API}/swarm/followup`, {
        prompt: p, mode,
        context: { scope_lines: result.scope_lines, findings: result.findings, uplift_usd: result.summary?.quantum_uplift_usd },
        file_text: attached?.excerpt || "",
      });
      setMessages((prev) => prev.map((m, i) => i === prev.length - 1 ? { role: "assistant", text: data.reply } : m));
      setResult((r) => r ? { ...r, followups: [...(r.followups || []), { q: p, a: data.reply }] } : r);
    } catch (_) {
      setMessages((prev) => prev.map((m, i) => i === prev.length - 1 ? { role: "assistant", text: "Sorry, I couldn't process that follow-up." } : m));
    }
  };

  const attachFile = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await axios.post(`${API}/swarm/upload`, fd); setAttached(data); }
    catch (_) { setAttached({ filename: file.name, kind: "file", chars: 0, excerpt: "" }); }
  };

  const selectTask = async (id) => {
    setActiveId(id); setSelectedFinding(null);
    let data = null;
    try {
      const cached = sessionStorage.getItem("qnneal_res_" + id);
      if (cached) data = JSON.parse(cached);
    } catch (_) {}
    if (!data) {
      try {
        const { data: doc } = await axios.get(`${API}/swarm/history/${id}`);
        data = doc.result;
        try { sessionStorage.setItem("qnneal_res_" + id, JSON.stringify(data)); } catch (_) {}
      } catch (_) { return; }
    }
    if (!data) return;
    setResult(data); setPanelOpen(false);
    if (data.roster_detail) { const m = {}; data.roster_detail.forEach((a) => { m[a.id] = a; }); stateRef.current.detail = m; }
    setMessages([
      { role: "user", text: data.prompt },
      { role: "assistant", text: (data.quantum_summary?.callout ? data.quantum_summary.callout + "\n\n" : "") + (data.executive_summary || "Analysis complete.") },
      { kind: "doc", runId: data.run_id, title: "Quantum Optimization Report", uplift: data.summary?.quantum_uplift_usd },
    ]);
    setFullChat(true);
    if (stateRef.current.run) { stateRef.current.run.settledAt = performance.now(); }
  };

  const renameTask = async (id, label) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, label } : t)));
    try { await axios.patch(`${API}/swarm/history/${id}`, { label }); } catch (_) {}
  };
  const deleteTask = async (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    if (id === activeId) newQuery();
    try { await axios.delete(`${API}/swarm/history/${id}`); } catch (_) {}
  };
  const clearHistory = async () => {
    setTasks([]); newQuery();
    try { await axios.delete(`${API}/swarm/history`); } catch (_) {}
  };
  const openDoc = () => { if (result) setDocOpen(true); };

  const newQuery = () => { setOpenProjectId(null); setActiveProjectId(null); setResult(null); setPanelOpen(false); setMessages([]); setActiveId(null); setPrompt(""); setAttached(null); setSelectedFinding(null); setFullChat(false); setDocOpen(null); stateRef.current.run = null; };

  const startTaskInProject = (qy, projectId) => { setOpenProjectId(null); setActiveProjectId(projectId); setWorkspaceKey((k) => k + 1); runOptimize(qy, projectId); };

  const loadStores = async (f = storeFilter) => {
    const params = new URLSearchParams(); Object.entries(f).forEach(([k, v]) => v && params.append(k, v));
    const { data } = await axios.get(`${API}/swarm/store-actions?${params.toString()}`);
    setStoreCards(data.cards); setStoreMeta({ regions: data.regions, action_types: data.action_types });
  };
  const openStores = async () => { setStoreOpen(true); await loadStores(); };

  const exportReport = async (fmt) => {
    if (!result) return; setExporting(true);
    try {
      let url;
      if (fmt === "pdf") {
        const { data } = await axios.post(`${API}/swarm/report.pdf`, { mode, prompt: result.prompt, executive_summary: result.executive_summary || "", findings: result.findings, comparison: result.comparison, summary: result.summary, compute_method: result.compute_method, followups: result.followups || [] }, { responseType: "blob" });
        url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      } else {
        const { data } = await axios.post(`${API}/swarm/export`, { format: fmt, mode, prompt: result.prompt, executive_summary: result.executive_summary || "", scope_lines: result.scope_lines, findings: result.findings, comparison: result.comparison, summary: result.summary, compute_method: result.compute_method, cost_intelligence: result.cost_intelligence, followups: result.followups || [] }, { responseType: "blob" });
        url = URL.createObjectURL(new Blob([data]));
      }
      const a = document.createElement("a");
      a.href = url; a.download = `QNNEAL_Report_${new Date().toISOString().slice(0, 10)}.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  const onMouse = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    stateRef.current.mouse = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const h = stateRef.current.hover;
    if (h) {
      const det = stateRef.current.detail[h.id];
      const enriched = det || { id: h.id, role: h.role, division: h.division, color: h.color,
        task: "Awaiting activation for this query scope.", output_summary: "Idle — no active task.",
        status: "idle", confidence: 90, duration_s: 0, model_label: h.model, routing_reason: "Routed to cheapest capable model",
        cost: 0, savings_percent: 0, data_sources: [], receives_from: "n/a", feeds_into: "n/a" };
      setHoverAgent({ ...enriched, color: h.color, mx: e.clientX - rect.left, my: e.clientY - rect.top });
    } else setHoverAgent(null);
  };

  const chips = PROMPTS[mode];

  const light = theme === "light";
  const wsTheme = light
    ? { bg: "#F1F5F9", panel: "#FFFFFF", border: "#E2E8F0", textH: "#0F172A", textB: "#334155", textDim: "#64748B", accent: "#0D9488" }
    : { bg: "#070B14", panel: "#0B1120", border: "#233047", textH: "#F1F5F9", textB: "#CBD5E1", textDim: "#94A3B8", accent: "#14F0C8" };

  return (
    <div className={`fixed inset-0 flex flex-col overflow-hidden bg-[#05070E] text-slate-100 ${theme === "light" ? "swarm-light" : ""}`} data-testid="swarm-page" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-b border-white/[0.06] bg-[#070B14]/85 backdrop-blur-xl z-30">
        <div className="flex items-center gap-3 text-[#14F0C8]">
          <button onClick={() => navigate("/dashboard")}
            data-testid="swarm-back-btn" title="Back to dashboard"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/[0.12] bg-[#0F1629]/70 text-slate-300 hover:border-[#14F0C8]/60 hover:text-[#14F0C8] transition-colors">
            <ArrowLeft className="w-4 h-4" /> <span className="text-xs font-medium">Dashboard</span>
          </button>
          <SwarmTopControls swarm={mode === "jnj" ? "pharmaceutical" : "retail"} accent="#14F0C8" panel="#0F1629" border="#233047" showBack={false} />
          <button onClick={() => setTheme(resolvedTheme === "light" ? "dark" : "light")}
            data-testid="theme-toggle-btn" title={resolvedTheme === "light" ? "Switch to dark mode" : "Switch to light mode"}
            className="flex items-center justify-center w-9 h-9 rounded-full border border-white/[0.12] bg-[#0F1629]/70 text-slate-300 hover:border-[#14F0C8]/60 hover:text-[#14F0C8] transition-colors">
            {resolvedTheme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </button>
          <Sparkles className="w-5 h-5" />
          <div>
            <div className="text-base font-bold tracking-wide">QNNEAL · Quantum Observatory</div>
            <div className="text-[11px] text-slate-400">{mode === "retail" ? "Retail · Multi-Brand Apparel Network" : "Pharmaceutical · Innovative Medicine · MedTech"}</div>
          </div>
        </div>
        {roster && (
          <div className="flex items-center gap-3">
            <button onClick={() => !running && setMode(mode === "retail" ? "jnj" : "retail")} disabled={running}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-700 bg-[#0F1629]/80 text-xs hover:border-[#14F0C8]/50 transition-colors disabled:opacity-40" data-testid="mode-toggle">
              {mode === "retail" ? <><Store className="w-4 h-4 text-[#14F0C8]" /> Retail</> : <><FlaskConical className="w-4 h-4 text-[#8B5CF6]" /> Pharmaceutical</>}
            </button>
            <div className="flex items-center gap-1 rounded-full border border-slate-700 bg-[#0F1629]/80 p-0.5" data-testid="role-switcher">
              {[["ceo", "CEO"], ["vp_ops", "VP"], ["store_manager", mode === "jnj" ? "Field Lead" : "Store Mgr"]].map(([r, l]) => (
                <button key={r} onClick={() => setRole(r)} className={`px-2.5 py-1 rounded-full text-[11px] transition-colors ${role === r ? "bg-[#14F0C8] text-[#07110E] font-semibold" : "text-slate-400 hover:text-slate-200"}`} data-testid={`role-${r}`}>{l}</button>
              ))}
            </div>
            <div className="text-right"><div className="text-xl font-bold text-slate-100" style={{ fontFamily: MONO }} data-testid="agent-total">{roster.total_agents}</div><div className="text-[10px] uppercase tracking-widest text-slate-500">agents</div></div>
          </div>
        )}
      </div>

      {/* Row: sidebar | stage | output panel */}
      <div className="flex flex-1 min-h-0">
        <SwarmWorkspace swarm={swarmKey} brand={mode === "jnj" ? "Pharma" : "Retail"} historyApi={`${API}/swarm`}
          theme={wsTheme} activeRunId={activeId} refreshKey={workspaceKey}
          onLoadTask={(id) => { setOpenProjectId(null); selectTask(id); }}
          onNewTask={newQuery}
          onOpenProject={(pid) => setOpenProjectId(pid)} />

        <div className="flex-1 min-w-0 flex flex-col">
        {openProjectId ? (
          <div className="flex-1 min-h-0 overflow-y-auto" style={{ background: wsTheme.bg }} data-testid="swarm-project-view-wrap">
            <SwarmProjectView projectId={openProjectId} theme={wsTheme} refreshSignal={workspaceKey}
              onStartTask={(qy) => startTaskInProject(qy, openProjectId)}
              onOpenTask={(id) => { setOpenProjectId(null); selectTask(id); }}
              onBack={() => setOpenProjectId(null)} />
          </div>
        ) : (
        <>
        {running && activeProjectId && <ProjectRunBanner projectId={activeProjectId} theme={wsTheme} />}
        <div ref={stageRef} className="relative flex-1 min-h-0" data-testid="swarm-stage">
          <canvas ref={canvasRef} onMouseMove={onMouse} className={`absolute inset-0 ${fullChat ? "hidden" : ""}`} data-testid="swarm-canvas" />
          {!fullChat && roster && (
            <div className="absolute left-5 top-5 space-y-1.5 pointer-events-none" data-testid="division-legend">
              {roster.divisions.map((d) => (
                <div key={d.code} className="flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5" style={{ background: d.color, boxShadow: `0 0 8px ${d.color}`, clipPath: "polygon(50% 0,93% 25%,93% 75%,50% 100%,7% 75%,7% 25%)" }} />
                  <span className="text-slate-300">{d.name}</span><span className="text-slate-600" style={{ fontFamily: MONO }}>{d.agent_count}</span>
                </div>
              ))}
            </div>
          )}
          {!fullChat && running && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center" data-testid="phase-status">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#14F0C8]/40 bg-[#0F1629]/80 backdrop-blur">
                <Zap className="w-4 h-4 text-[#14F0C8] animate-pulse" /><span className="text-sm font-semibold text-[#14F0C8]">{phaseLabel}</span>
              </div>
              <div className="text-xs text-slate-400 mt-2 max-w-md mx-auto" style={{ fontFamily: MONO }}>{statusText}</div>
              <div className="text-[11px] text-slate-500 mt-1.5" style={{ fontFamily: MONO }} data-testid="cost-ticker">
                {liveAgents} agents active · Cost so far: <span className="text-emerald-400">${liveCost.toFixed(2)}</span>
              </div>
            </div>
          )}
          {!fullChat && hoverAgent && <AgentTooltip agent={hoverAgent} x={hoverAgent.mx} y={hoverAgent.my} stageW={stateRef.current.w} />}
          {!fullChat && result && !panelOpen && (
            <button onClick={() => setPanelOpen(true)} data-testid="summary-pill"
              className="absolute bottom-5 right-5 z-30 flex items-center gap-3 px-4 py-2.5 rounded-full border border-[#14F0C8]/40 bg-[#0B1120]/90 backdrop-blur-xl shadow-2xl hover:border-[#14F0C8] transition-colors">
              <Sparkles className="w-4 h-4 text-[#14F0C8]" /><span className="text-xs text-slate-300">Quantum uplift</span>
              <span className="text-sm font-bold text-[#14F0C8]" style={{ fontFamily: MONO }}>{role === "store_manager" ? "restricted" : money(result.summary.quantum_uplift_usd)}</span>
              <Maximize2 className="w-3.5 h-3.5 text-slate-400" />
            </button>
          )}
          {fullChat && <FullChat messages={messages} onOpenDoc={openDoc} onOpenPanel={() => { setDocOpen(false); setPanelOpen(true); }} />}
        </div>

          <ChatDock messages={messages} prompt={prompt} setPrompt={setPrompt} onSend={runOptimize} onFollowup={sendFollowup}
            running={running} chips={chips} onPickChip={setPrompt} attached={attached} onAttach={attachFile}
            onClearAttach={() => setAttached(null)} hasResult={!!result} showTranscript={!fullChat} mode={mode} />
        </>
        )}
        </div>

        {!openProjectId && result && panelOpen && (
          <OutputPanel result={result} role={role} onClose={() => setPanelOpen(false)} onOpenStores={openStores}
            onExport={exportReport} exporting={exporting} onSelectFinding={setSelectedFinding}
            routingMode={routingMode} setRoutingMode={setRoutingMode} width={panelWidth} onResize={setPanelWidth} />
        )}
      </div>

      {storeOpen && <StorePanel cards={storeCards} meta={storeMeta} filter={storeFilter} setFilter={setStoreFilter} onReload={loadStores} onClose={() => setStoreOpen(false)} />}
      {selectedFinding && <FindingDetail finding={selectedFinding} onClose={() => setSelectedFinding(null)} />}
      {docOpen && result && <MarkdownDoc md={resultToMarkdown(result)} title={`QNNEAL_${result.mode || "report"}`} onClose={() => setDocOpen(false)} onExport={exportReport} exporting={exporting} />}
    </div>
  );
}
